import { useState, useEffect, useRef } from "react";
import {
  Smile, MapPin, AtSign, Plus, Loader2, AlertCircle, Facebook, Info, X,
} from "lucide-react";
import { api } from "@/lib/api";

const EMOJI_CATS = {
  Faccine: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","😚","😙","🥲","😋","😛","😜","🤪","😝","🤗","🤭","🤔","😐","😏","😒","🙄","😌","😔","😪","😴","😷","🥴","🥵","🥶","🤯","🥳","😎","🤓","🧐"],
  Cuori: ["❤️","🧡","💛","💚","💙","💜","🤎","🖤","🤍","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟","💌"],
  Mani: ["👋","🤚","✋","🖖","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","👇","☝️","👍","👎","✊","👊","🤛","🤜","👏","🙌","🫶","👐","🤲","🙏","💪","🤝"],
  Bellezza: ["💇‍♀️","💇‍♂️","💈","✂️","💄","💋","💅","💆‍♀️","💆‍♂️","👰","🤵","👱‍♀️","👱‍♂️","💃","🕺","🌹","🌺","🌷","💐","🌸","🌼","🌻","👗","👠","💍","👑"],
  Simboli: ["✨","🎉","🎊","🎁","🎈","🎂","💯","💥","💫","⭐","🌟","🔥","💦","💤","🔔","📣","💎","🏆","🎯","🎨","📸","📷","🗓️","📍","⏰","☎️","💬"],
};
export function ComposeToolbar({ onInsertEmoji, location, setLocation, userTags, setUserTags, showTagButton, tagsHint, locationEnabled = false }) {
  const [pane, setPane] = useState(null);   // 'emoji' | 'location' | 'tag' | null
  const [emojiCat, setEmojiCat] = useState("Faccine");
  const ref = useRef(null);
  useEffect(() => {
    if (!pane) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setPane(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pane]);
  const toggle = (k) => setPane((p) => (p === k ? null : k));

  return (
    <div className="absolute top-2 right-2 flex gap-1" ref={ref}>
      <ToolbarBtn active={pane === "emoji"} onClick={() => toggle("emoji")} title="Emoji"
        Icon={Smile} tid="publish-emoji-btn" />
      {locationEnabled && (
        <ToolbarBtn active={pane === "location"} onClick={() => toggle("location")}
          title="Aggiungi luogo" Icon={MapPin} tid="publish-loc-btn"
          badge={location ? "1" : null} />
      )}      {showTagButton && (
        <ToolbarBtn active={pane === "tag"} onClick={() => toggle("tag")}
          title="Tagga utenti Instagram" Icon={AtSign} tid="publish-tag-btn"
          badge={(userTags || []).filter(u => u.username).length || null} />
      )}

      {/* Emoji panel */}
      {pane === "emoji" && (
        <div className="absolute right-0 top-10 z-30 w-72 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl overflow-hidden"
             data-testid="publish-emoji-picker">
          <div className="flex border-b border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40">
            {Object.keys(EMOJI_CATS).map((k) => (
              <button key={k} type="button" onClick={() => setEmojiCat(k)}
                data-testid={`publish-emoji-cat-${k}`}
                className={`flex-1 px-1 py-1.5 text-[10px] font-bold border-b-2 transition-colors ${emojiCat === k ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text-primary)]"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="p-2 grid grid-cols-8 gap-1 max-h-56 overflow-y-auto">
            {EMOJI_CATS[emojiCat].map((e, i) => (
              <button key={i} type="button" onClick={() => onInsertEmoji(e)}
                data-testid={`publish-emoji-${e}`}
                className="h-8 w-8 rounded hover:bg-[color:var(--sp-subtle)] text-xl leading-none flex items-center justify-center">
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location panel */}
      {locationEnabled && pane === "location" && (
        <div className="absolute right-0 top-10 z-30 w-80 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl overflow-hidden p-3"
             data-testid="publish-location-panel">
          <LocationSearchInline value={location} onChange={(v) => { setLocation(v); if (v) setPane(null); }} />
        </div>
      )}

      {/* User tags panel */}
      {pane === "tag" && (
        <div className="absolute right-0 top-10 z-30 w-80 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl overflow-hidden p-3"
             data-testid="publish-tag-panel">
          <UserTagsInline value={userTags || []} onChange={setUserTags} hint={tagsHint} />
        </div>
      )}
    </div>
  );
}
function ToolbarBtn({ active, onClick, title, Icon, tid, badge }) {
  return (
    <button type="button" onClick={onClick} data-testid={tid} title={title}
      className={`relative h-8 w-8 rounded-lg border transition-colors flex items-center justify-center ${active ? "bg-[color:var(--sp-brand)] border-[color:var(--sp-brand)] text-white" : "bg-white border-[color:var(--sp-border)] text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] hover:border-[color:var(--sp-brand)]"}`}>
      <Icon className="h-4 w-4" />
      {badge && (
        <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[color:var(--sp-brand)] text-white text-[9px] font-bold flex items-center justify-center border border-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function LocationSearchInline({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const deb = useRef(null);
  useEffect(() => {
    if (value) return;
    if (q.trim().length < 2) { setResults([]); return; }
    clearTimeout(deb.current);
    deb.current = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const r = await api.get(`/meta/locations/search?q=${encodeURIComponent(q)}`);
        if (r.data.unavailable) setError(r.data.reason || "Ricerca luoghi non disponibile");
        setResults(r.data.data || []);
      } catch (e) { setError(e?.response?.data?.detail || "Errore"); }
      setLoading(false);
    }, 350);
    return () => clearTimeout(deb.current);
  }, [q, value]);

  if (value) {
    return (
      <div className="flex items-center gap-2" data-testid="publish-location-selected">
        <MapPin className="h-4 w-4 text-[color:var(--sp-brand)]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate">{value.name}</div>
          {value.address && <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{value.address}</div>}
        </div>
        <button type="button" onClick={() => { onChange(null); setQ(""); }}
          data-testid="publish-location-clear"
          className="p-1.5 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-red-50 hover:text-red-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div>
      <input type="text" value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
        data-testid="publish-location-input"
        className="sp-input text-sm" placeholder="Cerca un luogo…" />
      <div className="mt-2 max-h-56 overflow-y-auto -mx-3 px-3">
        {loading && <div className="p-2 text-xs text-[color:var(--sp-text-muted)] flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Ricerca…</div>}
        {error && (
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid="publish-location-error">
            <div className="font-bold text-amber-900 flex items-center gap-1 mb-1">
              <AlertCircle className="h-3 w-3" />
              {/expired|session|oauth/i.test(error) ? "Token Meta scaduto" : "Ricerca non disponibile"}
            </div>
            <div className="text-amber-800">{error}</div>
            {/expired|session|oauth/i.test(error) && (
              <a href="/app/settings#meta" className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-600 text-white text-[10px] font-bold hover:bg-amber-700">
                <Facebook className="h-3 w-3" /> Riconnetti
              </a>
            )}
          </div>
        )}
        {!loading && !error && q.length >= 2 && results.length === 0 && (
          <div className="p-2 text-xs text-[color:var(--sp-text-muted)]">Nessun luogo trovato</div>
        )}
        {results.map((r) => (
          <button key={r.id} type="button" onClick={() => onChange(r)}
            data-testid={`publish-location-option-${r.id}`}
            className="w-full flex items-start gap-2 px-2 py-1.5 text-left hover:bg-[color:var(--sp-subtle)]/60 rounded-lg">
            <MapPin className="h-3.5 w-3.5 mt-0.5 text-[color:var(--sp-text-muted)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{r.name}{r.verified && <span className="text-blue-500 ml-1">✓</span>}</div>
              {r.address && <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{r.address}</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
function UserTagsInline({ value, onChange, hint }) {
  const add = () => onChange([...(value || []), { username: "", x: 0.5, y: 0.5 }]);
  const update = (i, patch) => onChange(value.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-bold text-[color:var(--sp-text-secondary)]">Tag utenti Instagram (max 20)</div>
      {hint && (
        <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-[10px] text-amber-900 flex items-start gap-1"
             data-testid="publish-tags-hint">
          <Info className="h-3 w-3 flex-shrink-0 mt-0.5" /> {hint}
        </div>
      )}
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {value.length === 0 && (
          <div className="text-[11px] text-[color:var(--sp-text-muted)] py-1">Nessun tag</div>
        )}
        {value.map((t, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`publish-user-tag-${i}`}>
            <AtSign className="h-3 w-3 text-[color:var(--sp-text-muted)]" />
            <input type="text" value={t.username}
              onChange={(e) => update(i, { username: e.target.value })}
              data-testid={`publish-user-tag-username-${i}`}
              className="sp-input flex-1 text-sm py-1" placeholder="username" />
            <button type="button" onClick={() => remove(i)}
              data-testid={`publish-user-tag-remove-${i}`}
              className="p-1 rounded text-[color:var(--sp-text-muted)] hover:bg-red-50 hover:text-red-600">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        disabled={(value || []).length >= 20}
        data-testid="publish-user-tag-add"
        className="w-full py-1.5 text-xs font-bold text-[color:var(--sp-brand)] hover:bg-[color:var(--sp-subtle)]/60 rounded-lg inline-flex items-center justify-center gap-1 disabled:opacity-40">
        <Plus className="h-3 w-3" /> Aggiungi tag
      </button>
    </div>
  );
}

